import { toEDNStringFromSimpleObject } from "edn-data";
import { queryOmegaSingleResult } from "../api";

export type ModelProvider = "openrouter" | "anthropic";
export type LoginKind = "oauth-token" | "api-key";
export type LoginState = "none" | "valid" | "expiring" | "expired";

export interface LlmLoginSummary {
  present: boolean;
  kind?: LoginKind;
  "set-at"?: number;
  "expires-at"?: number;
}

export interface LlmAccountSummary {
  "account-id": string;
  "model-provider": ModelProvider;
  "login-from": string;
  login: LlmLoginSummary;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
// The agent service models an oauth-token as a one-year token (omega-ai-agent-service
// src/llmLogin/anthropicSignIn.ts reads Anthropic's one-year expires_in) and now reads an
// oauth-token whose expires-at is 0 or absent as EXPIRED (src/llmLogin/state.ts). A pasted setup
// token is the same kind of token; this page cannot read its exp, so it stamps that deployment
// lifetime rather than writing a zero the server would reject. An api-key is the no-expiry kind: 0.
const OAUTH_TOKEN_LIFETIME_MS = 365 * 24 * 60 * 60 * 1000;

const nounPath = (noun: "llm-account" | "llm-login" | "model", account?: string) =>
  account ? `#${noun}:${encodeURIComponent(account)}` : `#${noun}`;

// The Account record always reads as the redacted summary: never a secret.
export const readLlmAccount = async (account?: string): Promise<LlmAccountSummary> => {
  const query = `
    (datastore Qo.Api.Fr "omega/query-omega/api/fragment")
    (Qo.Api.Fr/resolve "system" "${nounPath("llm-account", account)}" Object)
    (return [Object])
  `;
  const record = (await queryOmegaSingleResult(query)) ?? {};
  const login = record.login ?? { present: false };
  return {
    "account-id": record["account-id"] ?? "",
    "model-provider": record["model-provider"] === "anthropic" ? "anthropic" : "openrouter",
    "login-from": record["login-from"] ?? "",
    login: login.present
      ? {
          present: true,
          kind: login.kind,
          "set-at": login["set-at"] ?? 0,
          "expires-at": login["expires-at"] ?? 0,
        }
      : { present: false },
  };
};

type FragmentPatch = Record<string, string | number | boolean | null>;

const writeFragment = async (
  noun: "llm-account" | "llm-login" | "model",
  account: string | undefined,
  patch: FragmentPatch,
) => {
  const edn = toEDNStringFromSimpleObject(patch, { keysAs: "string" });
  const query = `
    (datastore Qo.Api.Fr "omega/query-omega/api/fragment")
    (Qo.Api.Fr/write-data "system" "${nounPath(noun, account)}" ${edn} Result)
    (return [Result])
  `;
  return queryOmegaSingleResult(query);
};

export const writeLlmAccount = (account: string | undefined, patch: FragmentPatch) =>
  writeFragment("llm-account", account, patch);

// The Preferred Model Record (system:#model, or system:#model:KEY): the ONE model setting of an account, read and
// written by the CLI, the MCP and this UI alike (pages/oql/terms.html, Preferred Model).
export const writePreferredModel = (model: string, account?: string) =>
  writeFragment("model", account, { model });

// The one place a secret is written. It goes straight into the write and is never returned.
export const writeLlmLogin = async (account: string | undefined, kind: LoginKind, secret: string) => {
  const now = Date.now();
  return writeFragment("llm-login", account, {
    "login-kind": kind,
    "login-secret": secret,
    "set-at": now,
    "expires-at": kind === "oauth-token" ? now + OAUTH_TOKEN_LIFETIME_MS : 0,
  });
};

const deleteFragment = async (noun: "llm-account" | "llm-login" | "model", account?: string) => {
  const query = `
    (datastore Qo.Api.Fr "omega/query-omega/api/fragment")
    (Qo.Api.Fr/delete-data "system" "${nounPath(noun, account)}" Result)
    (return [Result])
  `;
  return queryOmegaSingleResult(query);
};

export const deleteLlmLogin = (account?: string) => deleteFragment("llm-login", account);

// Default: delete the row. The account then inherits (a service account takes its bound app owner's model, a user takes
// the agent service default). Same effect as `qo agent model --clear`; a delete of no row is a no-op (Qo.PreferredModel/clear!).
export const clearPreferredModel = (account?: string) => deleteFragment("model", account);

// The stored model of the account, or "" when it has none and inherits (path-query/preferred-model.oql answers a Record
// for every readable account, with "model" empty when there is no row and no owner row).
export const readPreferredModel = async (account?: string): Promise<string> => {
  const query = `
    (datastore Qo.Api.Fr "omega/query-omega/api/fragment")
    (Qo.Api.Fr/resolve "system" "${nounPath("model", account)}" Object)
    (return [Object])
  `;
  const record = (await queryOmegaSingleResult(query)) ?? {};
  return typeof record.model === "string" ? record.model : "";
};

export const loginState = (login: LlmLoginSummary, now = Date.now()): LoginState => {
  if (!login.present) return "none";
  const expiresAt = login["expires-at"] ?? 0;
  // Same rule as omega-ai-agent-service src/llmLogin/state.ts: 0 means "never" for an api-key,
  // but an oauth-token without a real expiry is a malformed/unset row the service refuses.
  if (!expiresAt) return login.kind === "oauth-token" ? "expired" : "valid";
  if (expiresAt <= now) return "expired";
  if (expiresAt - now <= THIRTY_DAYS_MS) return "expiring";
  return "valid";
};

// Render exactly none | valid | expiring | expired, and the expiry date.
export const renderLoginState = (login: LlmLoginSummary, now = Date.now()) => {
  const state = loginState(login, now);
  const expiresAt = login["expires-at"] ?? 0;
  return { state, expires: expiresAt ? new Date(expiresAt).toISOString().slice(0, 10) : "" };
};

// Assign reads each target's record first, then writes the borrow. One login, lent by reference.
export const assignLogin = async (myAccountId: string, targets: string[]) => {
  for (const target of targets) await readLlmAccount(target);
  for (const target of targets) {
    await writeLlmAccount(target, { "model-provider": "anthropic", "login-from": myAccountId });
  }
};

export const unlinkLogin = async (targets: string[]) => {
  for (const target of targets) await writeLlmAccount(target, { "login-from": "" });
};
